// Note: viewBox is zero-based (0 0 w h), NOT the symbol's own large x/y offset.
// Reusing the symbol's absolute offset on the wrapping <svg> causes Chrome to
// silently fail to paint the <use> content, so we only carry width/height here.
//
// `turnIn` / `turnOut` = heading change (degrees) this piece contributes,
// split into two independent halves instead of one symmetric value:
// `turnIn` is applied BEFORE the piece is drawn (so the kink is already
// visible right at its own entry point, snapping in rotated against
// whatever came before), and `turnOut` is applied AFTER, on top of turnIn,
// for whatever comes next. For a symmetric piece turnIn === turnOut, but
// they can differ (e.g. connectors whose shape isn't symmetric front-to-back).
//
// `vb` is the full artwork bbox (icon-<Id>) and is used for the picker
// buttons — it still includes the light-gray "Anschluss" stub that previews
// how a neighboring piece would attach, which is a helpful hint on a single
// icon. `buildVb` is the real physical-part bbox with that stub excluded
// (icon-<Id>-clean) and is what the assembled build-row uses for sizing.
//
// `entry`/`exit` are each piece's TRUE connecting-point coordinates in its
// own local (0,0)-(w,h) render box, extracted directly from the source
// artwork's actual edge vertices — NOT assumed to sit at the box corners.
// Several pieces are NOT simple rectangles: a domed Rainbow/Banana has its
// leg inset from the bbox edge, Pole Jam Cover's real mounting edge sits
// ~2 units in from the roof tip, and every turning connector has its exit
// edge cut at an angle so it lands well inside the bbox, not at x=w. Pinning
// placement to an assumed (0,h)/(w,h) corner — instead of this real point —
// is what caused the small notches/gaps at every joint in the first pass.
// Each piece is uniformly scaled (buildVb, preserving its own aspect ratio)
// so its true connecting-edge length equals MODULAR_BAUKASTEN_REF_HEIGHT
// (Flat's own leg length, used as reference since Flat is a plain rectangle
// with both edges already equal to its bbox height); `entry`/`exit` are the
// same source vertices carried through that same scale factor. For the
// asymmetric start/end-only pieces (Cover, Pole Jam Cover, UpElement3,
// Softbox3) only one real edge is ever a connection point regardless of
// role, so entry and exit are the same point there. When a piece renders
// flipped (flipOnStart/flipOnEnd), the local mirror swaps which natural
// point lands on the outer/connecting side — handled in renderBuildRow.
//
// All turnIn/turnOut, scaleOverride and yOffset values below were
// confirmed by iterative live visual testing (2026-09-07/08) in a
// standalone offline tuning tool with per-part angle/size/vertical-offset
// controls. That tool is not part of this file — only the resulting
// confirmed numbers are baked in here.
const MODULAR_BAUKASTEN_REF_HEIGHT = 20.373;
const MODULAR_BAUKASTEN_PARTS = {
  // Cover is unlike the other pieces: a narrow, TALL end-post (width << height)
  // rather than a wide/flat plank, so its "which edge connects" (position)
  // and "does the artwork look mirrored" (visual) needs are different from
  // everyone else and from each other:
  //  - Position: flipOnStart/flipOnEnd both true so its connection point
  //    sits at the FAR local edge (x=w) either way — that's what makes the
  //    next/previous piece dock flush against it instead of overlapping.
  //  - Visual: at the start it should render as-drawn (dome pointing up,
  //    per reference screenshot 2026-09-07), so visualFlipOnStart is false
  //    (overriding the position flip's default). At the end it should be
  //    mirrored top-to-bottom (dome pointing down), which needs the Y axis,
  //    not the X-axis mirror every other piece uses — hence flipAxis: 'y'.
  Cover: { label: 'Cover', code: 'C', sku: 'MADXXL', canStart: true, canEnd: true, flipOnStart: true, flipOnEnd: false, visualFlipOnStart: false, visualFlipOnEnd: true, flipAxis: 'x', vb: '0 0 15.359 33.130', buildVb: '0 0 1.903 22.257', entry: { x: 0, y: 22.257 }, exit: { x: 0, y: 22.257 }, turnIn: 0, turnOut: 0, scaleOverride: 0.74, yOffset: 3.8 },
  PoleJamCover: { label: 'Pole Jam Cover', code: 'P', sku: 'MADPJXXL', canStart: true, canEnd: false, flipOnStart: true, vb: '0 0 77.895 31.559', buildVb: '0 0 51.603 27.225', entry: { x: 2.121, y: 27.225 }, exit: { x: 2.121, y: 27.225 }, turnIn: 0, turnOut: 0, scaleOverride: 1, yOffset: 0 },
  UpElement3: { label: 'Up-Element 3m', code: 'U', sku: 'MUD3XXL', canStart: true, canEnd: true, flipOnEnd: true, vb: '0 0 78.002 22.352', buildVb: '0 0 82.748 21.350', entry: { x: 82.748, y: 21.350 }, exit: { x: 82.748, y: 21.350 }, turnIn: 0, turnOut: 0, scaleOverride: 0.954 },
  Softbox3: { label: 'Softbox 3m', code: 'S', sku: 'MSO3XXL', canStart: true, canEnd: true, flipOnEnd: true, vb: '0 0 78.119 22.712', buildVb: '0 0 81.609 21.415', entry: { x: 81.609, y: 21.415 }, exit: { x: 81.609, y: 21.415 }, turnIn: 0, turnOut: 0, scaleOverride: 0.951 },
  Flat1: { label: 'Flat 1m', code: 'F1', sku: 'MFL1XXL', isModule: true, vb: '0 0 34.413 23.373', buildVb: '0 0 31.482 20.418', entry: { x: 0, y: 20.418 }, exit: { x: 31.482, y: 20.418 }, turnIn: 0, turnOut: 0, scaleOverride: 1 },
  Flat3: { label: 'Flat 3m', code: 'F3', sku: 'MFL3XXL', isModule: true, vb: '0 0 77.413 23.373', buildVb: '0 0 74.577 20.418', entry: { x: 0, y: 20.418 }, exit: { x: 74.577, y: 20.418 }, turnIn: 0, turnOut: 0, scaleOverride: 1 },
  Flat4: { label: 'Flat 4m', code: 'F4', sku: 'MFL4XXL', isModule: true, vb: '0 0 94.413 23.373', buildVb: '0 0 91.615 20.418', entry: { x: 0, y: 20.418 }, exit: { x: 91.615, y: 20.418 }, turnIn: 0, turnOut: 0, scaleOverride: 1 },
  Rainbow3: { label: 'Rainbow 3m', code: 'R3', sku: 'MRA3XXL', isModule: true, vb: '0 0 83.203 26.524', buildVb: '0 0 83.369 24.453', entry: { x: 4.739, y: 24.452 }, exit: { x: 78.601, y: 24.452 }, turnIn: 12.9, turnOut: 13.5, scaleOverride: 0.94, yOffset: 0.2 },
  Rainbow4: { label: 'Rainbow 4m', code: 'R4', sku: 'MRA4XXL', isModule: true, vb: '0 0 93.799 27.524', buildVb: '0 0 90.057 24.323', entry: { x: 4.358, y: 24.324 }, exit: { x: 85.673, y: 24.324 }, turnIn: 12, turnOut: 12.4, scaleOverride: 0.94 },
  Banana3: { label: 'Banana 3m', code: 'B3', sku: 'MBA3XXL', isModule: true, vb: '0 0 77.341 20.847', buildVb: '0 0 87.219 20.939', entry: { x: 0, y: 20.804 }, exit: { x: 87.220, y: 20.804 }, turnIn: -12.3, turnOut: -13.4, scaleOverride: 0.95 },
  Banana4: { label: 'Banana 4m', code: 'B4', sku: 'MBA4XXL', isModule: true, vb: '0 0 91.040 22.847', buildVb: '0 0 93.003 20.966', entry: { x: 0, y: 20.845 }, exit: { x: 93.002, y: 20.845 }, turnIn: -11, turnOut: -11.5, scaleOverride: 0.95 },
  KinkedConnector: { label: 'Kinked-Verbinder', code: 'K', sku: 'MVK16XXL', isConnector: true, vb: '0 0 48.538 33.994', buildVb: '0 0 13.776 22.066', entry: { x: 0, y: 20.356 }, exit: { x: 7.268, y: 22.067 }, turnIn: 0, turnOut: 19, scaleOverride: 1 },
  ReversibleConnectorUP: { label: 'Reversible-Verbinder ↑', code: 'V', sku: 'MVKW16XXL', isConnector: true, vb: '0 0 45.120 35.053', buildVb: '0 0 16.441 24.245', entry: { x: 1.576, y: 21.968 }, exit: { x: 16.441, y: 24.245 }, turnIn: -19, turnOut: 0, scaleOverride: 1, yOffset: 0 },
  ReversibleConnectorDown: { label: 'Reversible-Verbinder ↓', code: 'W', sku: 'MVKW16XXL', isConnector: true, vb: '0 0 45.258 32.721', buildVb: '0 0 14.866 21.638', entry: { x: 0, y: 20.311 }, exit: { x: 8.329, y: 21.637 }, turnIn: 0, turnOut: 19, scaleOverride: 1, yOffset: 0 },
  DoubleKinkedConnector: { label: 'Double-Kinked-Verbinder (2m)', code: 'D', sku: 'MVDK16XXL', isConnector: true, vb: '0 0 73.530 38.140', buildVb: '0 0 32.340 25.071', entry: { x: 0, y: 20.282 }, exit: { x: 25.355, y: 25.028 }, turnIn: 0, turnOut: 19, scaleOverride: 1 },
  StepConnector: { label: 'Step-Verbinder', code: 'T', sku: 'MVSXXL', isConnector: true, vb: '0 0 71.336 30.179', buildVb: '0 0 1.471 26.368', entry: { x: 0, y: 20.416 }, exit: { x: 1.471, y: 26.367 }, turnIn: 0, turnOut: 0, scaleOverride: 1 },
};

// Reverse lookup for decoding a typed-in configuration code back into a
// sequence of part ids. Codes are either a single letter (parts with only
// one size/variant) or a letter followed by a digit (parts available in
// multiple lengths, e.g. Flat/Rainbow/Banana) — never two letters — so
// parsing a code string just means: read one letter, and if the next
// character is a digit, it belongs to the same part.
const MODULAR_BAUKASTEN_CODE_TO_ID = {};
Object.keys(MODULAR_BAUKASTEN_PARTS).forEach((id) => {
  MODULAR_BAUKASTEN_CODE_TO_ID[MODULAR_BAUKASTEN_PARTS[id].code] = id;
});

// One-time setup: give every piece an editable `scale` factor (from
// scaleOverride if a confirmed value was tested and recorded above,
// otherwise falling back to the uniform-height normalization formula:
// REF_HEIGHT / its own buildVb height), plus a `yOffset` (vertical nudge
// in local units, applied on top of the piece's own entry/exit y after
// scaling) defaulting to 0. Both are then live-editable below.
Object.keys(MODULAR_BAUKASTEN_PARTS).forEach((id) => {
  const part = MODULAR_BAUKASTEN_PARTS[id];
  if (part.scaleOverride !== undefined) {
    part.scale = part.scaleOverride;
  } else {
    const rawH = Number(part.buildVb.split(' ')[3]);
    part.scale = MODULAR_BAUKASTEN_REF_HEIGHT / rawH;
  }
  if (part.yOffset === undefined) part.yOffset = 0;
});

const MODULAR_BAUKASTEN_PROFILE_LABELS = {
  '05': 'Box',
  '06': 'Butterbox',
  '03': 'Rail',
};

class ModularBaukasten extends HTMLElement {
  constructor() {
    super();
    this.profile = '05';
    this.sequence = [];
    this.finished = false;
    // Map-style view state for the build row: zoom=1/pan={0,0} means
    // "auto-fit everything", exactly like before this feature existed.
    this.zoom = 1;
    this.pan = { x: 0, y: 0 };
    this.dragging = false;
  }

  connectedCallback() {
    this.buildRow = this.querySelector('[data-build-row]');
    this.emptyState = this.querySelector('[data-empty-state]');
    this.pickerHeading = this.querySelector('[data-picker-heading]');
    this.pickerOptions = this.querySelector('[data-picker-options]');
    this.partsList = this.querySelector('[data-parts-list]');
    this.partsEmpty = this.querySelector('[data-parts-empty]');
    this.addToCartBtn = this.querySelector('[data-add-to-cart]');
    this.undoBtn = this.querySelector('[data-undo-btn]');
    this.resetBtn = this.querySelector('[data-reset-btn]');
    this.successEl = this.querySelector('[data-success]');
    this.variantId = this.dataset.variantId;
    this.zoomInBtn = this.querySelector('[data-zoom-in]');
    this.zoomOutBtn = this.querySelector('[data-zoom-out]');
    this.zoomResetBtn = this.querySelector('[data-zoom-reset]');
    this.configCodeEl = this.querySelector('[data-config-code]');
    this.codeInput = this.querySelector('[data-code-input]');
    this.codeLoadBtn = this.querySelector('[data-code-load-btn]');

    // Text for the picker/alerts is passed in from Liquid as data attributes
    // (translated via the theme's locale files) since this custom element's
    // JS has no access to Liquid's `t` filter at runtime. Falls back to the
    // German copy if an attribute is missing, so nothing breaks if the
    // element markup hasn't been updated yet.
    this.i18n = {
      pickerStartHeading: this.dataset.i18nPickerStartHeading || 'Wähle ein Startstück',
      pickerBuildingHeading: this.dataset.i18nPickerBuildingHeading || 'Füge ein weiteres Teil hinzu',
      pickerEndLabel: this.dataset.i18nPickerEndLabel || 'oder abschließen mit:',
      pickerFinishedHeading:
        this.dataset.i18nPickerFinishedHeading || 'Deine Line ist fertig — jetzt zur Anfrage hinzufügen oder zurücksetzen.',
      addToCartError:
        this.dataset.i18nAddToCartError ||
        'Die Zusammenstellung konnte nicht zur Anfrage hinzugefügt werden. Bitte versuche es erneut.',
      codeUnknownPrefix: this.dataset.i18nCodeUnknownPrefix || 'Unbekannter Code-Abschnitt:',
    };

    this.querySelectorAll('.modular-baukasten__profile-btn').forEach((btn) => {
      btn.addEventListener('click', () => this.setProfile(btn.dataset.profile));
    });

    this.undoBtn.addEventListener('click', () => this.undo());
    this.resetBtn.addEventListener('click', () => this.reset());
    this.addToCartBtn.addEventListener('click', () => this.addToCart());
    if (this.codeLoadBtn) this.codeLoadBtn.addEventListener('click', () => this.loadFromCode());

    if (this.zoomInBtn) this.zoomInBtn.addEventListener('click', () => this.setZoom(this.zoom * 1.25));
    if (this.zoomOutBtn) this.zoomOutBtn.addEventListener('click', () => this.setZoom(this.zoom / 1.25));
    if (this.zoomResetBtn)
      this.zoomResetBtn.addEventListener('click', () => {
        this.zoom = 1;
        this.pan = { x: 0, y: 0 };
        this.renderBuildRow();
      });

    // Map-style pan: mouse-wheel to zoom (centered on cursor), drag to pan.
    this.buildRow.addEventListener('wheel', (e) => this.onWheel(e), { passive: false });
    this.buildRow.addEventListener('pointerdown', (e) => this.onPointerDown(e));
    this.buildRow.addEventListener('pointermove', (e) => this.onPointerMove(e));
    this.buildRow.addEventListener('pointerup', (e) => this.onPointerUp(e));
    this.buildRow.addEventListener('pointercancel', (e) => this.onPointerUp(e));
    this.buildRow.style.cursor = 'grab';
    this.buildRow.style.touchAction = 'none';

    this.render();
  }

  setZoom(next) {
    this.zoom = Math.max(0.2, Math.min(10, next));
    this.renderBuildRow();
  }

  // Converts a pixel delta (from a mouse/pointer event) into build-row
  // viewBox units, using the currently rendered SVG's own size/viewBox —
  // needed so panning tracks the cursor 1:1 regardless of zoom level.
  pixelDeltaToViewBoxDelta(dxPx, dyPx) {
    const svg = this.buildRow.querySelector('svg');
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    const vb = svg.viewBox.baseVal;
    if (!rect.width || !rect.height) return { x: 0, y: 0 };
    return { x: (dxPx / rect.width) * vb.width, y: (dyPx / rect.height) * vb.height };
  }

  onWheel(e) {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    this.setZoom(this.zoom * factor);
  }

  onPointerDown(e) {
    if (this.sequence.length === 0) return;
    this.dragging = true;
    this.dragStart = { x: e.clientX, y: e.clientY, pan: { ...this.pan } };
    this.buildRow.setPointerCapture(e.pointerId);
    this.buildRow.style.cursor = 'grabbing';
  }

  onPointerMove(e) {
    if (!this.dragging) return;
    const dxPx = e.clientX - this.dragStart.x;
    const dyPx = e.clientY - this.dragStart.y;
    const delta = this.pixelDeltaToViewBoxDelta(dxPx, dyPx);
    // Dragging right should reveal content to the left, so subtract.
    this.pan = { x: this.dragStart.pan.x - delta.x, y: this.dragStart.pan.y - delta.y };
    this.renderBuildRow();
  }

  onPointerUp() {
    this.dragging = false;
    this.buildRow.style.cursor = 'grab';
  }

  setProfile(profile) {
    this.profile = profile;
    this.querySelectorAll('.modular-baukasten__profile-btn').forEach((btn) => {
      btn.setAttribute('aria-pressed', btn.dataset.profile === profile ? 'true' : 'false');
    });
    this.render();
  }

  // Derives what the picker should offer next, purely from sequence + finished.
  // After the start piece, every step offers connectors, modules and end
  // pieces together — no forced alternation, any order/repeat is fine.
  nextExpectation() {
    if (this.finished) return 'finished';
    if (this.sequence.length === 0) return 'start';
    return 'building';
  }

  addPart(id, isEndChoice) {
    this.sequence.push(id);
    this.finished = !!isEndChoice;
    this.successEl.hidden = true;
    this.render();
  }

  undo() {
    this.sequence.pop();
    this.finished = false;
    this.successEl.hidden = true;
    this.render();
  }

  reset() {
    this.sequence = [];
    this.finished = false;
    this.successEl.hidden = true;
    this.zoom = 1;
    this.pan = { x: 0, y: 0 };
    this.render();
  }

  buildSku(id) {
    const part = MODULAR_BAUKASTEN_PARTS[id];
    return part.sku.replace('XX', this.profile);
  }

  render() {
    const expectation = this.nextExpectation();
    this.renderBuildRow();
    this.renderPicker(expectation);
    this.renderPartsList();

    this.undoBtn.hidden = this.sequence.length === 0;
    this.resetBtn.hidden = this.sequence.length === 0;
    this.addToCartBtn.disabled = !(this.sequence.length > 0 && this.finished);

    if (this.configCodeEl) {
      this.configCodeEl.textContent = this.sequence.length > 0 ? this.buildConfigCode() : '—';
    }
  }

  // Chains pieces end-to-end. Each piece is drawn rotated by
  // `heading + turnIn` (so the kink from the PREVIOUS piece is already
  // visible right at this piece's own entry point), and the running
  // heading handed to the next piece advances by `turnIn + turnOut`
  // (the piece's full contribution). turnIn/turnOut can differ, so a
  // piece doesn't have to bend symmetrically around its own center.
  renderBuildRow() {
    this.buildRow.innerHTML = '';
    if (this.sequence.length === 0) {
      this.buildRow.appendChild(this.emptyState);
      return;
    }

    const DEG = Math.PI / 180;
    let heading = 0;
    let pos = { x: 0, y: 0 };
    let minX = 0,
      minY = 0,
      maxX = 0,
      maxY = 0;
    const updateBounds = (x, y) => {
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    };

    let inner = '';
    // Tracks every joint (piece-to-piece docking point) in global
    // coordinates, in order, so the ground shape below can trace the
    // chain's real up/down shape instead of cutting a straight line
    // from the very first to the very last point.
    const groundPath = [{ x: 0, y: 0 }];
    // Set below only when the LAST piece is a true end-cap (entry === exit,
    // e.g. Cover, Pole Jam Cover, Up-Element, Softbox — see comment at its
    // computation) — `pos` never advances for those, even though their
    // artwork still extends well past it (Up-Element's ramp is ~79 units
    // long). Ground shape uses this point instead of `pos` when set.
    let lastFarPoint = null;
    this.sequence.forEach((id, index) => {
      const part = MODULAR_BAUKASTEN_PARTS[id];
      const [, , rawW] = part.buildVb.split(' ').map(Number);
      // Uniformly scale each piece (preserving its own aspect ratio) using
      // its own editable `scale` factor (see live editor below), instead of
      // trusting the raw bounding-box size directly — that's what kept a
      // tall bounding box (e.g. a piece whose artwork includes support feet
      // below its platform) from rendering oversized relative to a plain
      // piece like Flat.
      const scale = part.scale;
      const rawH = Number(part.buildVb.split(' ')[3]);
      const w = rawW * scale;
      const h = rawH * scale;
      // yOffset nudges a piece's connecting edge up/down (in local units,
      // applied after scaling) — useful for pieces whose true connecting
      // edge sits at a slightly different height than its raw artwork
      // vertices suggest, so it docks flush with the top edge of the piece
      // before/after it instead of appearing to sit lower or higher.
      const scaledPoint = (p) => ({ x: p.x * scale, y: p.y * scale + (part.yOffset || 0) });
      const iconId = `${id}-clean`;
      const isFirst = index === 0;
      const isLast = index === this.sequence.length - 1;
      // `flipped` (position) decides which local point becomes the outer
      // connection point — always mirrored on X regardless of the piece's
      // own shape, since that's purely about which edge of the piece is
      // "outward" along the chain. Kept separate from `visualFlipped`
      // (below) because for a piece like Cover the two don't agree: it
      // needs the X-mirrored connection point on BOTH ends (so neighbors
      // dock flush instead of overlapping it), but should only look
      // mirrored — and on the Y axis, not X — at the end, not the start.
      const flipped = (isFirst && part.flipOnStart) || (isLast && this.finished && part.flipOnEnd);

      const mirror = (p) => ({ x: w - p.x, y: p.y });
      const scaledEntry = scaledPoint(part.entry);
      const scaledExit = scaledPoint(part.exit);
      const entry = flipped ? mirror(scaledExit) : scaledEntry;
      const exit = flipped ? mirror(scaledEntry) : scaledExit;
      const rel = { x: exit.x - entry.x, y: exit.y - entry.y };

      const visualFlipOnStart = part.visualFlipOnStart !== undefined ? part.visualFlipOnStart : part.flipOnStart;
      const visualFlipOnEnd = part.visualFlipOnEnd !== undefined ? part.visualFlipOnEnd : part.flipOnEnd;
      const visualFlipped = (isFirst && visualFlipOnStart) || (isLast && this.finished && visualFlipOnEnd);
      const flipAxis = part.flipAxis || 'x';

      const turnIn = part.turnIn || 0;
      const turnOut = part.turnOut || 0;
      const pieceHeading = heading + turnIn;
      const rad = pieceHeading * DEG;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      const rotRel = { x: rel.x * cos - rel.y * sin, y: rel.x * sin + rel.y * cos };
      const newPos = { x: pos.x + rotRel.x, y: pos.y + rotRel.y };

      [
        [0, 0],
        [w, 0],
        [0, h],
        [w, h],
      ].forEach(([lx, ly]) => {
        const dx = lx - entry.x;
        const dy = ly - entry.y;
        const rx = dx * cos - dy * sin;
        const ry = dx * sin + dy * cos;
        updateBounds(pos.x + rx, pos.y + ry);
      });

      // True end-cap pieces have entry === exit (their only connection
      // point), so `rel` is zero and `pos` never advances to cover their
      // own body. Their real far edge sits at the SAME local y as entry,
      // mirrored to the opposite local x edge (entry.x is always ~0 or
      // ~w for these) — compute that point here, in this piece's own
      // rotation frame, instead of trying to reconstruct it afterwards.
      if (isLast && rel.x === 0 && rel.y === 0) {
        const farLocalX = w - entry.x;
        const fdx = farLocalX - entry.x;
        const frx = fdx * cos;
        const fry = fdx * sin;
        lastFarPoint = { x: pos.x + frx, y: pos.y + fry };
      }

      const placeTransform = `translate(${pos.x.toFixed(2)},${pos.y.toFixed(2)}) rotate(${pieceHeading.toFixed(2)}) translate(${-entry.x},${-entry.y})`;
      const visualMirrorTransform =
        flipAxis === 'y'
          ? `translate(0,${h}) scale(1,-1)`
          : flipAxis === 'both'
            ? `translate(${w},${h}) scale(-1,-1)`
            : `translate(${w},0) scale(-1,1)`;
      const content = visualFlipped
        ? `<g transform="${visualMirrorTransform}"><use href="#icon-${iconId}" width="${w}" height="${h}"></use></g>`
        : `<use href="#icon-${iconId}" width="${w}" height="${h}"></use>`;
      inner += `<g transform="${placeTransform}">${content}</g>`;

      pos = newPos;
      groundPath.push({ x: pos.x, y: pos.y });
      heading += turnIn + turnOut;
    });

    // Ground/slope sketch: a simple snow-surface fill behind the obstacles.
    // The top edge traces every joint in `groundPath` (not just first/last),
    // so it follows the chain's actual up/down shape — e.g. a Rainbow hump
    // in the middle — instead of cutting a straight line underneath it and
    // leaving a gap. Flat on the left of piece 0's entry, then continues
    // past the last piece's exit in the same direction the chain was
    // heading (`heading`, i.e. the last piece's own exit slope) a bit
    // further right before dropping to the bottom. Drawn first (prepended
    // to `inner`) so every piece renders on top of it.
    const groundMarginLeft = 40;
    const groundExtra = 40;
    const groundRad = heading * DEG;
    // For normal pass-through pieces (Flat, Rainbow, Banana, connectors)
    // `pos` already IS the true exit point, so behavior is unchanged from
    // before this fix. Only overridden when the last piece is a true
    // end-cap (see `lastFarPoint` above).
    const groundAnchor = lastFarPoint || pos;
    const groundEnd = {
      x: groundAnchor.x + groundExtra * Math.cos(groundRad),
      y: groundAnchor.y + groundExtra * Math.sin(groundRad),
    };
    const groundLeft = Math.min(minX, 0) - groundMarginLeft;
    const groundBottom = Math.max(maxY, groundAnchor.y, groundEnd.y) + 40;
    const groundTrace = groundPath.slice(0, -1).concat([groundAnchor]);
    const groundPoints = [
      `${groundLeft},${groundBottom}`,
      `${groundLeft},0`,
      ...groundTrace.map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`),
      `${groundEnd.x.toFixed(2)},${groundEnd.y.toFixed(2)}`,
      `${groundEnd.x.toFixed(2)},${groundBottom}`,
    ].join(' ');
    inner = `<polygon points="${groundPoints}" fill="#00a3de" opacity="0.3" />` + inner;
    updateBounds(groundLeft, 0);
    updateBounds(groundEnd.x, groundEnd.y);

    const pad = 15;
    const fitX = minX - pad;
    const fitY = minY - pad;
    const fitW = maxX - minX + pad * 2;
    const fitH = maxY - minY + pad * 2;

    // Map-style zoom/pan on top of the auto-fit box: zoom=1/pan={0,0}
    // reproduces the plain auto-fit view exactly as before this feature
    // existed; zooming shrinks/grows the visible box around its center,
    // panning shifts that center.
    const centerX = fitX + fitW / 2 + this.pan.x;
    const centerY = fitY + fitH / 2 + this.pan.y;
    const viewW = fitW / this.zoom;
    const viewH = fitH / this.zoom;
    const vbX = centerX - viewW / 2;
    const vbY = centerY - viewH / 2;

    this.buildRow.innerHTML = `<svg viewBox="${vbX.toFixed(2)} ${vbY.toFixed(2)} ${viewW.toFixed(2)} ${viewH.toFixed(2)}">${inner}</svg>`;
  }

  renderPicker(expectation) {
    this.pickerOptions.innerHTML = '';

    if (expectation === 'finished') {
      this.pickerHeading.textContent = this.i18n.pickerFinishedHeading;
      return;
    }

    if (expectation === 'start') {
      this.pickerHeading.textContent = this.i18n.pickerStartHeading;
      const choices = Object.keys(MODULAR_BAUKASTEN_PARTS).filter((id) => MODULAR_BAUKASTEN_PARTS[id].canStart);
      this.renderChoiceButtons(choices, 'flipOnStart', false);
      return;
    }

    // building: connectors + modules together, plus an end group — no
    // forced alternation, everything is always available.
    this.pickerHeading.textContent = this.i18n.pickerBuildingHeading;
    const connectors = Object.keys(MODULAR_BAUKASTEN_PARTS).filter((id) => MODULAR_BAUKASTEN_PARTS[id].isConnector);
    const modules = Object.keys(MODULAR_BAUKASTEN_PARTS).filter((id) => MODULAR_BAUKASTEN_PARTS[id].isModule);
    this.renderChoiceButtons([...connectors, ...modules], null, false);

    const endLabel = document.createElement('div');
    endLabel.className = 'modular-baukasten__picker-subheading';
    endLabel.textContent = this.i18n.pickerEndLabel;
    this.pickerOptions.appendChild(endLabel);
    const endChoices = Object.keys(MODULAR_BAUKASTEN_PARTS).filter((id) => MODULAR_BAUKASTEN_PARTS[id].canEnd);
    this.renderChoiceButtons(endChoices, 'flipOnEnd', true);
  }

  renderChoiceButtons(ids, flipField, isEndChoice) {
    ids.forEach((id) => {
      const part = MODULAR_BAUKASTEN_PARTS[id];
      const flipped = flipField && part[flipField];
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'modular-baukasten__picker-btn' + (flipped ? ' modular-baukasten__picker-btn--flipped' : '');
      btn.innerHTML = `<svg viewBox="${part.vb}"><use href="#icon-${id}"></use></svg><span>${part.label}</span>`;
      btn.addEventListener('click', () => this.addPart(id, isEndChoice));
      this.pickerOptions.appendChild(btn);
    });
  }

  renderPartsList() {
    this.partsList.querySelectorAll('li:not([data-parts-empty])').forEach((el) => el.remove());
    if (this.sequence.length === 0) {
      this.partsEmpty.hidden = false;
      return;
    }
    this.partsEmpty.hidden = true;
    const counts = {};
    this.sequence.forEach((id) => {
      counts[id] = (counts[id] || 0) + 1;
    });
    Object.keys(counts).forEach((id) => {
      const part = MODULAR_BAUKASTEN_PARTS[id];
      const li = document.createElement('li');
      const sku = this.buildSku(id);
      li.innerHTML = `<span>${counts[id]}× ${part.label}</span><span>${sku}</span>`;
      this.partsList.appendChild(li);
    });
  }

  buildCompositionText() {
    const counts = {};
    this.sequence.forEach((id) => {
      counts[id] = (counts[id] || 0) + 1;
    });
    return Object.keys(counts)
      .map((id) => {
        const part = MODULAR_BAUKASTEN_PARTS[id];
        return `${counts[id]}x ${part.label} (${this.buildSku(id)})`;
      })
      .join('\n');
  }

  // Builds the short, human-readable configuration code: each part's `code`
  // (see MODULAR_BAUKASTEN_PARTS above) concatenated in build order, so the
  // code itself encodes exactly which piece comes where — not just counts.
  buildConfigCode() {
    return this.sequence.map((id) => MODULAR_BAUKASTEN_PARTS[id].code).join('');
  }

  // Reverse of buildConfigCode(): parses a typed-in code back into a part-id
  // sequence and rebuilds the line from it. A code letter is followed by a
  // digit only when that part has multiple lengths (see the comment above
  // MODULAR_BAUKASTEN_CODE_TO_ID), so lookahead on the next character alone
  // is enough to know whether to consume 1 or 2 characters at a time.
  loadFromCode() {
    const raw = (this.codeInput.value || '').trim().toUpperCase();
    if (!raw) return;
    const parsed = [];
    let i = 0;
    while (i < raw.length) {
      const nextIsDigit = /[0-9]/.test(raw[i + 1] || '');
      const chunk = raw.slice(i, i + (nextIsDigit ? 2 : 1));
      const id = MODULAR_BAUKASTEN_CODE_TO_ID[chunk];
      if (!id) {
        alert(`${this.i18n.codeUnknownPrefix} "${raw.slice(i)}"`);
        return;
      }
      parsed.push(id);
      i += chunk.length;
    }
    if (parsed.length === 0) return;
    this.sequence = parsed;
    this.finished = true;
    this.successEl.hidden = true;
    this.codeInput.value = '';
    this.render();
  }

  addToCart() {
    if (!this.variantId || this.sequence.length === 0 || !this.finished) return;
    this.addToCartBtn.disabled = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: parseInt(this.variantId, 10),
            quantity: 1,
            properties: {
              Profil: MODULAR_BAUKASTEN_PROFILE_LABELS[this.profile],
              Zusammenstellung: this.buildCompositionText(),
              'Konfigurations-Code': this.buildConfigCode(),
            },
          },
        ],
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('cart add failed');
        return res.json();
      })
      .then(() => {
        // Deliberately not calling reset() here: the customer/store owner
        // should still see exactly what was just added (build, parts list,
        // code) instead of it being cleared away right after success.
        this.successEl.hidden = false;
        this.render();
      })
      .catch(() => {
        this.addToCartBtn.disabled = false;
        alert(this.i18n.addToCartError);
      });
  }
}

customElements.define('modular-baukasten', ModularBaukasten);
